import { useSignal, useSignals } from '@preact/signals-react/runtime';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAuth } from '~/auth/use-auth';
import { UUID } from '~/utils/uuid';
import type { Message, ToolCall } from './types';
import { useChatApi } from './use-chat-api';
import { useChatFiles } from './use-chat-files';
import { useChatMessages } from './use-chat-messages';
import { useChatSocket } from './use-chat-socket';
import { useChatThinking } from './use-chat-thinking';

type UseChatProps = {
  initialChatId?: string;
  initialMessage?: string | null;
  agentUrn?: string;
  isOnboarding?: boolean;
};

export function useChat({
  initialChatId,
  initialMessage,
  agentUrn,
  isOnboarding = false,
}: UseChatProps) {
  useSignals();
  const { user } = useAuth();

  const messages = useChatMessages({ initialChatId });
  const files = useChatFiles();
  const thinking = useChatThinking();

  const api = useChatApi({
    chatId: messages.chatId.value,
    agentUrn,
    onMessageSent: () => {
      files.clearFiles();
    },
    onResponseReceived: (message) => {
      messages.addMessage(message);
    },
  });

  useChatSocket({
    chatId: messages.chatId.value,
    onThinking: thinking.handleThinkingMessage,
    onAssistant: (data) => {
      messages.addOrUpdateAssistantMessage(
        data.message.content,
        data.message.status,
      );
    },
  });

  const [onboardingSent, setOnboardingSent] = useState(false);

  const input = useSignal('');

  // biome-ignore lint/correctness/useExhaustiveDependencies: onboarding logic needs specific dependencies to prevent infinite loops
  useEffect(() => {
    if (isOnboarding && user && !onboardingSent) {
      thinking.setInitialThinking();
      api.sendOnboardingMessage(user.profile).then(() => {
        setOnboardingSent(true);
      });
    }
  }, [isOnboarding, user, onboardingSent]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: initialMessage is meant to run only once on mount
  useEffect(() => {
    if (initialMessage) {
      input.value = initialMessage;
      handleSubmit();
    }
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    input.value = event.currentTarget.value;
  };

  const handleSubmit = async (data?: Pick<Message, 'tool_call_id'>) => {
    const value = input.value.trim();
    if (!value) return;

    if (messages.isInitial) messages.setIsInitial(false);

    const userMessage: Message = {
      id: UUID(),
      type: 'human',
      content: value,
      status: 'finished',
    };

    if (data?.tool_call_id) {
      userMessage.type = 'tool';
      userMessage.tool_call_id = data.tool_call_id;
    }

    messages.addMessage(userMessage);
    input.value = '';

    await api.sendMessage(userMessage);

    setTimeout(() => {
      thinking.clearThinking();
    }, 1000);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: handleSubmit is stable and doesn't need to be in dependencies
  const handleToolCall = useCallback(
    async (action: ToolCall, response: string) => {
      if (api.isLoading.value) return;
      input.value = response;
      await handleSubmit({
        tool_call_id: action.id,
      });
    },
    [],
  );

  const currentMessageHasToolCall = useMemo(() => {
    const lastMessage =
      messages.messages.value[messages.messages.value.length - 1];
    return lastMessage?.tool_calls && lastMessage.tool_calls.length > 0;
  }, [messages.messages.value]);

  return {
    messages: messages.messages,
    input,
    handleInputChange,
    handleSubmit,
    isLoading: api.isLoading,
    attachedFiles: files.attachedFiles,
    handleFileSelect: files.handleFileSelect,
    removeFile: files.removeFile,
    status: api.status,
    handleToolCall,
    thinkings: thinking.thinkings,
    isInitial: messages.isInitial,
    currentMessageHasToolCall,
  };
}
